/**
 * Chart Table Transformer - DYNAMIC N-SIZE ROW
 *
 * Supports:
 * - Cluster Bar Chart (with n-size row)
 * - Stacked Bar Chart (Horizontal - with n-size row)
 * - Stacked Column Chart (with n-size row)
 * - Vertical Cluster Column Chart (with n-size row)
 * - ANY hierarchy depth (1-4+ levels)
 * - N-size row is always the last row, dynamically positioned
 */

import numeral from "numeral";
import { tableConfig } from "../tableConfig";
import { IStatMarker } from "src/pages/Insights/models/interfaces";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SeriesData {
	name: string;
	originalName: string;
	value: number | string;
	n_size: number | string;
	stat_marker?: {
		enabled: boolean;
		ci_rows: Array<{
			ci_level: string;
			items: Array<{
				compared_with: string;
				marker_label: string;
				p_value: number;
				direction: string;
			}>;
		}>;
	};
}

interface ChartDataItem {
	xAxis: string;
	xAxisOriginal: string;
	series: SeriesData[];
	n_size: { min: number | string; max: number | string };
}

type ChartData = ChartDataItem[] | Record<string, any>;

export interface ColumnConfig {
	title: string | React.ReactNode;
	dataIndex?: string;
	key: string;
	fixed?: "left" | "right";
	width?: number;
	align?: "left" | "center" | "right";
	isPercentage?: boolean;
	children?: ColumnConfig[];
	_specialty?: string;
	_nSizeData?: {
		minValue: number | string;
		maxValue: number | string;
		highlightMin: boolean;
		highlightMax: boolean;
	};
	_isEvenGroup?: boolean;
	_attributeName?: string;
	_hierarchyLevel?: number;
	_isLeafLevel?: boolean;
	_hasNSizeRow?: boolean;
	rowSpan?: number;
	_isFlatData?: boolean;
}

export interface TableRow {
	key: string;
	attribute?: string;
	wave?: string;
	attributeRowSpan?: number;
	isLastAttributeRow?: boolean;
	_statMarkers?: Record<string, IStatMarker>; // keyed by dataIndex
	_nSizes?: Record<string, number | string>; // keyed by dataIndex; mirrors _statMarkers shape
	[key: string]: any;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getColumnWidthFromConfig = (columnType: "attribute" | "wave" | "numerical"): number => {
	const config = tableConfig.getConfig();
	return config.columns[columnType].minWidth;
};

const isFlatArray = (data: any): data is ChartDataItem[] => {
	return Array.isArray(data) && data.length > 0 && data[0]?.xAxis !== undefined;
};

const isChartDataItemArray = (value: any): value is ChartDataItem[] => {
	return Array.isArray(value) && value.length > 0 && value[0]?.xAxis !== undefined && value[0]?.series !== undefined;
};

const detectHierarchyLevel = (data: ChartData): number => {
	if (isFlatArray(data)) return 0;

	const checkDepth = (obj: any, currentDepth: number = 0): number => {
		if (isChartDataItemArray(obj)) return currentDepth;
		if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
			const keys = Object.keys(obj);
			if (keys.length === 0) return currentDepth;
			const depths = keys.map((key) => checkDepth(obj[key], currentDepth + 1));
			return Math.max(...depths);
		}
		return currentDepth;
	};

	return checkDepth(data);
};

const isPercentageData = (data: ChartData): boolean => {
	const checkItems = (items: ChartDataItem[]) => {
		return items.every((item) =>
			item.series.every((s) => {
				if (s.value === "No data" || s.value === null || s.value === undefined) return true;
				return typeof s.value === "number" && s.value >= 0 && s.value <= 1;
			}),
		);
	};

	if (isFlatArray(data)) return checkItems(data);

	const checkNested = (obj: any): boolean => {
		for (const value of Object.values(obj)) {
			if (isChartDataItemArray(value)) {
				if (!checkItems(value)) return false;
			} else if (typeof value === "object" && value !== null) {
				if (!checkNested(value)) return false;
			}
		}
		return true;
	};

	return checkNested(data);
};

const assignStatMarker = (row: TableRow, dataIndex: string, seriesData: SeriesData): void => {
	if (seriesData.stat_marker?.enabled && seriesData.stat_marker.ci_rows?.some((r) => r.items.length > 0)) {
		if (!row._statMarkers) row._statMarkers = {};
		row._statMarkers[dataIndex] = seriesData.stat_marker as unknown as IStatMarker;
	}
};

const assignNSize = (row: TableRow, dataIndex: string, seriesData: SeriesData | undefined): void => {
	if (!seriesData) return;
	if (seriesData.n_size === undefined || seriesData.n_size === null) return;
	if (!row._nSizes) row._nSizes = {};
	row._nSizes[dataIndex] = seriesData.n_size as number | string;
};

export const formatValue = (value: number | string, labelFormat: string = "##"): string => {
	if (value === "No data" || value === null || value === undefined) return "-";
	if (typeof value !== "number" || isNaN(value)) return "-";
	return numeral(value).format(labelFormat);
};

const extractWaveNames = (data: ChartData): string[] => {
	const waves = new Set<string>();
	const extractFromItems = (items: ChartDataItem[]) => {
		items.forEach((item) => {
			if (item.series) item.series.forEach((s) => waves.add(s.name));
		});
	};

	if (isFlatArray(data)) {
		extractFromItems(data);
	} else {
		const extractNested = (obj: any) => {
			for (const value of Object.values(obj)) {
				if (isChartDataItemArray(value)) extractFromItems(value);
				else if (typeof value === "object" && value !== null) extractNested(value);
			}
		};
		extractNested(data);
	}
	return Array.from(waves);
};

const extractAttributes = (data: ChartData): string[] => {
	const attributes = new Set<string>();
	const extractFromItems = (items: ChartDataItem[]) => {
		items.forEach((item) => attributes.add(item.xAxis));
	};

	if (isFlatArray(data)) {
		extractFromItems(data);
	} else {
		const extractNested = (obj: any) => {
			for (const value of Object.values(obj)) {
				if (isChartDataItemArray(value)) extractFromItems(value);
				else if (typeof value === "object" && value !== null) extractNested(value);
			}
		};
		extractNested(data);
	}
	return Array.from(attributes);
};

/**
 * Pull non-zero, non-"No data" n_size values from a single chart-data item.
 * Prefers per-series n_size (so a "No data" series doesn't fold into a
 * row-level { min: 0, max: X } aggregate) and falls back to the row's
 * own n_size if series-level values aren't usable.
 */
const collectNonZeroNSizes = (item: any, out: number[]): void => {
	if (Array.isArray(item?.series)) {
		let found = false;
		item.series.forEach((s: any) => {
			const v = s?.n_size;
			if (v === null || v === undefined) return;
			if (typeof v === "string" && v.toLowerCase() === "no data") return;
			const n = Number(v);
			if (!isNaN(n) && n > 0) {
				out.push(n);
				found = true;
			}
		});
		if (found) return;
	}

	const nSize = item?.n_size;
	if (nSize === undefined || nSize === null) return;
	if (typeof nSize === "object") {
		const rMin = Number(nSize.min);
		const rMax = Number(nSize.max);
		if (!isNaN(rMin) && rMin > 0) out.push(rMin);
		if (!isNaN(rMax) && rMax > 0) out.push(rMax);
	} else {
		const n = Number(nSize);
		if (!isNaN(n) && n > 0) out.push(n);
	}
};

const getNSizeData = (items: any): { minValue: number | string; maxValue: number | string; highlightMin: boolean; highlightMax: boolean } => {
	if (!isChartDataItemArray(items) || items.length === 0) {
		return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
	}

	const values: number[] = [];
	items.forEach((item) => collectNonZeroNSizes(item, values));

	if (values.length > 0) {
		const minValue = Math.min(...values);
		const maxValue = Math.max(...values);
		return {
			minValue,
			maxValue,
			highlightMin: minValue < 30,
			highlightMax: maxValue < 30,
		};
	}

	return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
};

const getFlatDataNSizeRange = (items: ChartDataItem[]): { minValue: number; maxValue: number; highlightMin: boolean; highlightMax: boolean } => {
	if (!items || items.length === 0) return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };

	const values: number[] = [];
	items.forEach((item) => collectNonZeroNSizes(item, values));

	if (values.length > 0) {
		const minSize = Math.min(...values);
		const maxSize = Math.max(...values);
		return {
			minValue: minSize,
			maxValue: maxSize,
			highlightMin: minSize < 30,
			highlightMax: maxSize < 30,
		};
	}

	return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
};

/**
 * Get n-size for a specific attribute (xAxis) within items.
 * Uses row-level n_size to match chart view behavior.
 * Used for STACKED COLUMN charts.
 */
const getAttributeNSize = (
	items: ChartDataItem[],
	attributeName: string,
): { minValue: number | string; maxValue: number | string; highlightMin: boolean; highlightMax: boolean } => {
	if (!isChartDataItemArray(items) || items.length === 0) {
		return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
	}

	const item = items.find((itm) => itm.xAxis === attributeName);
	if (!item) {
		return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
	}

	const values: number[] = [];
	collectNonZeroNSizes(item, values);

	if (values.length > 0) {
		const minValue = Math.min(...values);
		const maxValue = Math.max(...values);
		return {
			minValue,
			maxValue,
			highlightMin: minValue > 0 && minValue < 30,
			highlightMax: maxValue > 0 && maxValue < 30,
		};
	}

	return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
};

/**
 * Get n-size range across all items for a given series group.
 * Uses row-level n_size to match chart view behavior.
 * Used for CLUSTER COLUMN charts.
 */
const getSeriesNSizeRange = (
	items: ChartDataItem[],
	_seriesName: string,
): { minValue: number | string; maxValue: number | string; highlightMin: boolean; highlightMax: boolean } => {
	if (!isChartDataItemArray(items) || items.length === 0) {
		return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
	}

	const values: number[] = [];
	items.forEach((item) => collectNonZeroNSizes(item, values));

	if (values.length > 0) {
		const minSize = Math.min(...values);
		const maxSize = Math.max(...values);
		return {
			minValue: minSize,
			maxValue: maxSize,
			highlightMin: minSize < 30,
			highlightMax: maxSize < 30,
		};
	}

	return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
};

const calculateColumnWidths = (
	totalNumericalColumns: number,
	hasWaveColumn: boolean,
	hasAttributeColumn: boolean,
): { attribute: string; wave: string; numerical: string; totalColumns: number } => {
	const ATTRIBUTE_WIDTH = 20;
	const WAVE_WIDTH = 10;
	const TARGET_VISIBLE_NUMERICAL = 10;

	let totalColumns = totalNumericalColumns;
	if (hasAttributeColumn) totalColumns += 1;
	if (hasWaveColumn) totalColumns += 1;

	const remainingWidth = 100 - (hasAttributeColumn ? ATTRIBUTE_WIDTH : 0) - (hasWaveColumn ? WAVE_WIDTH : 0);
	const numericalWidth = remainingWidth / TARGET_VISIBLE_NUMERICAL;

	return {
		attribute: `${ATTRIBUTE_WIDTH}%`,
		wave: `${WAVE_WIDTH}%`,
		numerical: `${numericalWidth}%`,
		totalColumns,
	};
};

const countNumericalColumns = (columns: ColumnConfig[]): number => {
	let count = 0;

	const countRecursive = (cols: ColumnConfig[]) => {
		cols.forEach((col) => {
			if (col.children && col.children.length > 0) {
				countRecursive(col.children);
			} else if (col.dataIndex && col.dataIndex !== "attribute" && col.dataIndex !== "wave") {
				count++;
			}
		});
	};

	countRecursive(columns);
	return count;
};

/**
 * Calculate maximum depth of column tree (to determine rowSpan)
 */
const calculateMaxDepth = (columns: ColumnConfig[]): number => {
	let maxDepth = 1;

	const getDepth = (cols: ColumnConfig[], currentDepth: number = 1): number => {
		let max = currentDepth;

		cols.forEach((col) => {
			if (col.children && col.children.length > 0) {
				const childDepth = getDepth(col.children, currentDepth + 1);
				max = Math.max(max, childDepth);
			}
		});

		return max;
	};

	maxDepth = getDepth(columns);
	return maxDepth;
};

/**
 * Wrap leaf columns to add n-size row
 * For h_cluster_bar  flat data: don't wrap (single row header with only n-size)
 * For v_stacked_column, h_stacked_bar and v_cluster_column: wrap to create 3-row header structure
 */
const wrapLeafColumnsForNSizeRow = (columns: ColumnConfig[], isFlatData: boolean, chartType?: string): ColumnConfig[] => {
	// For h_cluster_bar and h_stacked_bar flat data, don't wrap - just mark columns
	if (isFlatData && chartType === "h_cluster_bar") {
		return columns.map((col) => {
			if (col.dataIndex === "attribute" || col.dataIndex === "wave") {
				return col;
			}
			return {
				...col,
				_isFlatData: true,
			};
		});
	}

	// For other chart types (stacked column, cluster column), wrap normally
	const wrappedColumns = columns.map((col) => {
		if (col.children && col.children.length > 0) {
			return {
				...col,
				children: wrapLeafColumnsForNSizeRow(col.children, false, chartType),
			};
		}

		if (col.dataIndex === "attribute" || col.dataIndex === "wave") {
			return col;
		}

		const nSizeData = (col as any)._nSizeData;

		return {
			title: col.title,
			key: `${col.key}_wrapper`,
			align: col.align,
			_hierarchyLevel: (col as any)._hierarchyLevel,
			_isEvenGroup: (col as any)._isEvenGroup,
			_hasNSizeRow: true,
			children: [
				{
					title: "N_SIZE",
					dataIndex: col.dataIndex,
					key: col.key,
					width: col.width,
					align: col.align,
					isPercentage: col.isPercentage,
					_nSizeData: nSizeData,
					_isEvenGroup: (col as any)._isEvenGroup,
					_attributeName: (col as any)._attributeName,
					_hierarchyLevel: ((col as any)._hierarchyLevel || 0) + 1,
					_isLeafLevel: true,
				},
			],
		};
	});

	const maxDepth = calculateMaxDepth(wrappedColumns);

	return wrappedColumns.map((col) => {
		if (col.dataIndex === "attribute" || col.dataIndex === "wave") {
			return {
				...col,
				rowSpan: maxDepth,
			};
		}
		return col;
	});
};

// ============================================================================
// CLUSTER BAR CHART FUNCTIONS
// ============================================================================

const generateClusterBarColumns = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[]): ColumnConfig[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isPercentage = isPercentageData(chartData);
	const columns: ColumnConfig[] = [];

	columns.push({
		title: "",
		dataIndex: "attribute",
		key: "attribute",
		fixed: "left",
		width: getColumnWidthFromConfig("attribute"),
		align: "center" as const,
	});

	columns.push({
		title: "",
		dataIndex: "wave",
		key: "wave",
		fixed: "left",
		width: getColumnWidthFromConfig("wave"),
		align: "center" as const,
	});

	if (hierarchyLevel === 0) {
		const nSizeData = getFlatDataNSizeRange(chartData as ChartDataItem[]);

		columns.push({
			title: "", // Empty title, will show only n-size
			dataIndex: "value",
			key: "value",
			width: getColumnWidthFromConfig("numerical"),
			align: "center" as const,
			isPercentage,
			_nSizeData: {
				minValue: nSizeData.minValue,
				maxValue: nSizeData.maxValue,
				highlightMin: nSizeData.highlightMin,
				highlightMax: nSizeData.highlightMax,
			},
			_hierarchyLevel: 0,
			_isLeafLevel: true,
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);

		level1Keys.forEach((key1, index) => {
			const items = chartData[key1];
			const nSizeData = getNSizeData(items);

			columns.push({
				title: key1,
				dataIndex: key1,
				key: key1,
				width: getColumnWidthFromConfig("numerical"),
				align: "center" as const,
				isPercentage,
				_specialty: key1,
				_nSizeData: {
					minValue: nSizeData.minValue,
					maxValue: nSizeData.maxValue,
					highlightMin: nSizeData.highlightMin,
					highlightMax: nSizeData.highlightMax,
				},
				_isEvenGroup: index % 2 === 0,
				_hierarchyLevel: 0,
				_isLeafLevel: true,
			});
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const childColumns: ColumnConfig[] = level2Keys.map((key2) => {
				const items = chartData[key1][key2];
				const nSizeData = getNSizeData(items);

				const isEven = columnGroupIndex % 2 === 0;
				columnGroupIndex++;

				return {
					title: key2,
					dataIndex: `${key1}__${key2}`,
					key: `${key1}__${key2}`,
					width: getColumnWidthFromConfig("numerical"),
					align: "center" as const,
					isPercentage,
					_specialty: key2,
					_nSizeData: {
						minValue: nSizeData.minValue,
						maxValue: nSizeData.maxValue,
						highlightMin: nSizeData.highlightMin,
						highlightMax: nSizeData.highlightMax,
					},
					_isEvenGroup: isEven,
					_hierarchyLevel: 1,
					_isLeafLevel: true,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: childColumns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);

		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);

				const level3Columns: ColumnConfig[] = level3Keys.map((key3) => {
					const items = chartData[key1][key2][key3];
					const nSizeData = getNSizeData(items);

					const isEven = columnGroupIndex % 2 === 0;
					columnGroupIndex++;

					return {
						title: key3,
						dataIndex: `${key1}__${key2}__${key3}`,
						key: `${key1}__${key2}__${key3}`,
						width: getColumnWidthFromConfig("numerical"),
						align: "center" as const,
						isPercentage,
						_specialty: key3,
						_nSizeData: {
							minValue: nSizeData.minValue,
							maxValue: nSizeData.maxValue,
							highlightMin: nSizeData.highlightMin,
							highlightMax: nSizeData.highlightMax,
						},
						_isEvenGroup: isEven,
						_hierarchyLevel: 2,
						_isLeafLevel: true,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: level3Columns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);

				const level3Columns: ColumnConfig[] = level3Keys.map((key3) => {
					const level4Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2][key3]), filterHierarchy, 3);

					const level4Columns: ColumnConfig[] = level4Keys.map((key4) => {
						const items = chartData[key1][key2][key3][key4];
						const nSizeData = getNSizeData(items);

						const isEven = columnGroupIndex % 2 === 0;
						columnGroupIndex++;

						return {
							title: key4,
							dataIndex: `${key1}__${key2}__${key3}__${key4}`,
							key: `${key1}__${key2}__${key3}__${key4}`,
							width: getColumnWidthFromConfig("numerical"),
							align: "center" as const,
							isPercentage,
							_specialty: key4,
							_nSizeData: {
								minValue: nSizeData.minValue,
								maxValue: nSizeData.maxValue,
								highlightMin: nSizeData.highlightMin,
								highlightMax: nSizeData.highlightMax,
							},
							_isEvenGroup: isEven,
							_hierarchyLevel: 3,
							_isLeafLevel: true,
						};
					});

					return {
						title: key3,
						key: `${key1}__${key2}__${key3}`,
						align: "center" as const,
						children: level4Columns,
						_hierarchyLevel: 2,
						_isLeafLevel: false,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: level3Columns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	}

	return columns;
};

const generateClusterBarTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[]): TableRow[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const waves = extractWaveNames(chartData);
	// Reorder y-axis attributes (xAxis values) using filter_hierarchy at the leaf depth.
	const attributes = resolveKeysByHierarchyOrder(extractAttributes(chartData), filterHierarchy, hierarchyLevel);
	const tableData: TableRow[] = [];
	let rowIndex = 0;

	if (hierarchyLevel === 0) {
		const totalItems = (chartData as ChartDataItem[]).length;
		(chartData as ChartDataItem[]).forEach((item, itemIndex) => {
			const seriesLength = item.series.length;
			const isLastAttributeGroup = itemIndex === totalItems - 1;

			item.series.forEach((s, waveIdx) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					wave: s.name,
					value: s.value === "No data" ? null : s.value,
				};

				if (waveIdx === 0) {
					row.attribute = item.xAxis;
					row.attributeRowSpan = seriesLength;
				} else {
					row.attributeRowSpan = 0;
				}

				// Mark last row of the LAST attribute group only
				if (waveIdx === seriesLength - 1 && isLastAttributeGroup) {
					row.isLastAttributeRow = true;
				}

				// Stat marker for this cell
				assignStatMarker(row, "value", s);
				assignNSize(row, "value", s);

				tableData.push(row);
			});
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const wavesLength = waves.length;

			waves.forEach((wave, waveIdx) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					wave,
				};

				if (waveIdx === 0) {
					row.attribute = attribute;
					row.attributeRowSpan = wavesLength;
				} else {
					row.attributeRowSpan = 0;
				}

				// Mark last row of the last attribute group only
				if (waveIdx === wavesLength - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				level1Keys.forEach((key1) => {
					const items = chartData[key1];
					if (isChartDataItemArray(items)) {
						const item = items.find((itm) => itm.xAxis === attribute);
						if (item) {
							const seriesData = item.series.find((s) => s.name === wave);
							row[key1] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
							if (seriesData) {
								assignStatMarker(row, key1, seriesData);
								assignNSize(row, key1, seriesData);
							}
						} else {
							row[key1] = null;
						}
					}
				});

				tableData.push(row);
			});
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const wavesLength = waves.length;

			waves.forEach((wave, waveIdx) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					wave,
				};

				if (waveIdx === 0) {
					row.attribute = attribute;
					row.attributeRowSpan = wavesLength;
				} else {
					row.attributeRowSpan = 0;
				}

				// Mark last row of the last attribute group only
				if (waveIdx === wavesLength - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				level1Keys.forEach((key1) => {
					const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

					level2Keys.forEach((key2) => {
						const items = chartData[key1][key2];
						if (isChartDataItemArray(items)) {
							const item = items.find((itm) => itm.xAxis === attribute);
							if (item) {
								const seriesData = item.series.find((s) => s.name === wave);
								row[`${key1}__${key2}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
								if (seriesData) {
									assignStatMarker(row, `${key1}__${key2}`, seriesData);
									assignNSize(row, `${key1}__${key2}`, seriesData);
								}
							} else {
								row[`${key1}__${key2}`] = null;
							}
						}
					});
				});

				tableData.push(row);
			});
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const wavesLength = waves.length;

			waves.forEach((wave, waveIdx) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					wave,
				};

				if (waveIdx === 0) {
					row.attribute = attribute;
					row.attributeRowSpan = wavesLength;
				} else {
					row.attributeRowSpan = 0;
				}

				// Mark last row of the last attribute group only
				if (waveIdx === wavesLength - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				level1Keys.forEach((key1) => {
					const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
					level2Keys.forEach((key2) => {
						const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);
						level3Keys.forEach((key3) => {
							const items = chartData[key1][key2][key3];
							if (isChartDataItemArray(items)) {
								const item = items.find((itm) => itm.xAxis === attribute);
								if (item) {
									const seriesData = item.series.find((s) => s.name === wave);
									row[`${key1}__${key2}__${key3}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
									if (seriesData) {
										assignStatMarker(row, `${key1}__${key2}__${key3}`, seriesData);
										assignNSize(row, `${key1}__${key2}__${key3}`, seriesData);
									}
								} else {
									row[`${key1}__${key2}__${key3}`] = null;
								}
							}
						});
					});
				});

				tableData.push(row);
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const wavesLength = waves.length;

			waves.forEach((wave, waveIdx) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					wave,
				};

				if (waveIdx === 0) {
					row.attribute = attribute;
					row.attributeRowSpan = wavesLength;
				} else {
					row.attributeRowSpan = 0;
				}

				// Mark last row of the last attribute group only
				if (waveIdx === wavesLength - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				level1Keys.forEach((key1) => {
					const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
					level2Keys.forEach((key2) => {
						const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);
						level3Keys.forEach((key3) => {
							const level4Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2][key3]), filterHierarchy, 3);
							level4Keys.forEach((key4) => {
								const items = chartData[key1][key2][key3][key4];
								if (isChartDataItemArray(items)) {
									const item = items.find((itm) => itm.xAxis === attribute);
									if (item) {
										const seriesData = item.series.find((s) => s.name === wave);
										row[`${key1}__${key2}__${key3}__${key4}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
										if (seriesData) {
											assignStatMarker(row, `${key1}__${key2}__${key3}__${key4}`, seriesData);
											assignNSize(row, `${key1}__${key2}__${key3}__${key4}`, seriesData);
										}
									} else {
										row[`${key1}__${key2}__${key3}__${key4}`] = null;
									}
								}
							});
						});
					});
				});

				tableData.push(row);
			});
		});
	}

	return tableData;
};

export const getHClusterBarChartColumns = (chartData: ChartData, chartType?: string, filterHierarchy?: IHierarchyDefLite[]): ColumnConfig[] => {
	const columns = generateClusterBarColumns(chartData, filterHierarchy);
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isFlatData = hierarchyLevel === 0;

	const wrappedColumns = wrapLeafColumnsForNSizeRow(columns, isFlatData, chartType);

	const totalNumericalCols = countNumericalColumns(wrappedColumns);
	const hasWave = wrappedColumns.some((c) => c.dataIndex === "wave");
	const hasAttribute = wrappedColumns.some((c) => c.dataIndex === "attribute");

	(wrappedColumns as any)._widthMetadata = calculateColumnWidths(totalNumericalCols, hasWave, hasAttribute);

	return wrappedColumns;
};

export const getHClusterBarChartTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[]): TableRow[] => {
	return generateClusterBarTableData(chartData, filterHierarchy);
};

export const getClusterBarChartSummary = (chartData: ChartData) => {
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const waves = extractWaveNames(chartData);
	const attributes = extractAttributes(chartData);

	return {
		hierarchyLevel,
		totalAttributes: attributes.length,
		totalWaves: waves.length,
		totalRows: attributes.length * waves.length,
		waves,
		attributes,
	};
};

/**
 * Extract stat marker labels from a series data item's ci_rows.
 * Returns a concatenated string of unique marker labels, or undefined if none found.
 */
const extractStatMarkerLabels = (seriesData: SeriesData): string | undefined => {
	if (!seriesData.stat_marker?.enabled || !seriesData.stat_marker.ci_rows) {
		return undefined;
	}

	const labels: string[] = [];
	seriesData.stat_marker.ci_rows.forEach((ciRow) => {
		if (ciRow.items && ciRow.items.length > 0) {
			ciRow.items.forEach((ciItem) => {
				if (ciItem.marker_label && !labels.includes(ciItem.marker_label)) {
					labels.push(ciItem.marker_label);
				}
			});
		}
	});

	return labels.length > 0 ? labels.join(",") : undefined;
};

/**
 * Collect stat markers for all series in a chart data item, keyed by dataIndex.
 * @param seriesList - The series array from a ChartDataItem
 * @param dataIndexFn - Function to derive the dataIndex key from a series entry
 * Returns a Record to assign to row._statMarkers, or undefined if no markers found.
 */
const collectStatMarkers = (seriesList: SeriesData[], dataIndexFn: (seriesData: SeriesData) => string): Record<string, IStatMarker> | undefined => {
	const markers: Record<string, IStatMarker> = {};

	seriesList.forEach((seriesData) => {
		if (seriesData.stat_marker?.enabled && seriesData.stat_marker.ci_rows?.some((r) => r.items.length > 0)) {
			markers[dataIndexFn(seriesData)] = seriesData.stat_marker as unknown as IStatMarker;
		}
	});

	return Object.keys(markers).length > 0 ? markers : undefined;
};

/**
 * Collect stat markers from pre-mapped series entries.
 * Each entry pairs a SeriesData with its target dataIndex key.
 */
const collectStatMarkersFromPairs = (pairs: Array<{ seriesData: SeriesData; dataIndex: string }>): Record<string, IStatMarker> | undefined => {
	const markers: Record<string, IStatMarker> = {};

	pairs.forEach(({ seriesData, dataIndex }) => {
		if (seriesData.stat_marker?.enabled && seriesData.stat_marker.ci_rows?.some((r) => r.items.length > 0)) {
			markers[dataIndex] = seriesData.stat_marker as unknown as IStatMarker;
		}
	});

	return Object.keys(markers).length > 0 ? markers : undefined;
};

const collectNSizes = (seriesList: SeriesData[], dataIndexFn: (seriesData: SeriesData) => string): Record<string, number | string> | undefined => {
	const nSizes: Record<string, number | string> = {};
	seriesList.forEach((seriesData) => {
		if (seriesData.n_size !== undefined && seriesData.n_size !== null) {
			nSizes[dataIndexFn(seriesData)] = seriesData.n_size as number | string;
		}
	});
	return Object.keys(nSizes).length > 0 ? nSizes : undefined;
};

const collectNSizesFromPairs = (pairs: Array<{ seriesData: SeriesData; dataIndex: string }>): Record<string, number | string> | undefined => {
	const nSizes: Record<string, number | string> = {};
	pairs.forEach(({ seriesData, dataIndex }) => {
		if (seriesData.n_size !== undefined && seriesData.n_size !== null) {
			nSizes[dataIndex] = seriesData.n_size as number | string;
		}
	});
	return Object.keys(nSizes).length > 0 ? nSizes : undefined;
};

// ============================================================================
// AUTHORITATIVE ORDER RESOLVERS
// ============================================================================

/**
 * Minimal hierarchy definition shape needed for ordering.
 * Kept local to avoid importing from interfaces and coupling the transformer.
 */
interface IHierarchyDefLite {
	order: number;
	values: string[];
	orientation?: "column" | "row";
	dimension?: string;
	[key: string]: any;
}

/**
 * Reorder discovered series names using the authoritative seriesOrder
 * derived from chart_config.ser_color. Any series not present in the
 * authoritative list are appended in their discovery order so nothing is dropped.
 */
const resolveSeriesOrder = (discoveredWaves: string[], seriesOrder?: string[]): string[] => {
	if (!seriesOrder || seriesOrder.length === 0) return discoveredWaves;

	const discoveredSet = new Set(discoveredWaves);
	const ordered: string[] = [];

	// First: authoritative order, but only for waves actually present in data
	seriesOrder.forEach((name) => {
		if (discoveredSet.has(name)) {
			ordered.push(name);
			discoveredSet.delete(name);
		}
	});

	// Then: any stragglers from discovered list (preserves their original order)
	discoveredWaves.forEach((name) => {
		if (discoveredSet.has(name)) ordered.push(name);
	});

	return ordered;
};

/**
 * Reorder discovered keys at a given nesting depth (0-based) using the
 * authoritative values from filter_hierarchy.
 *
 * `depth` is the transformer's 0-based nesting position (depth=0 is the
 * outermost data key, depth=1 is the next level, and so on). filter_hierarchy
 * entries may use arbitrary `order` values (e.g. 1, 2, ...) and mixed
 * orientations — both column-oriented and row-oriented dimensions can appear
 * as nested data keys depending on the backend's payload shape. We sort
 * hierarchies by `order` ascending and pick the entry at the given depth.
 *
 * Stragglers (keys not present in the hierarchy definition) are appended
 * in their original discovery order so nothing is dropped.
 */
const resolveKeysByHierarchyOrder = (discoveredKeys: string[], filterHierarchy: IHierarchyDefLite[] | undefined, depth: number): string[] => {
	if (!filterHierarchy || filterHierarchy.length === 0) return discoveredKeys;

	// Sort hierarchies by `order` ascending, then pick by ordinal position.
	// This normalizes 1-based `order` from the backend to 0-based `depth`.
	const sortedHierarchies = [...filterHierarchy].sort((a, b) => a.order - b.order);
	const hierarchyDef = sortedHierarchies[depth];

	if (!hierarchyDef || !Array.isArray(hierarchyDef.values) || hierarchyDef.values.length === 0) {
		return discoveredKeys;
	}

	const discoveredSet = new Set(discoveredKeys);
	const ordered: string[] = [];

	// First: authoritative order, but only for keys actually present in data
	hierarchyDef.values.forEach((val) => {
		if (discoveredSet.has(val)) {
			ordered.push(val);
			discoveredSet.delete(val);
		}
	});

	// Then: any stragglers not in the hierarchy (preserves original discovery order)
	discoveredKeys.forEach((k) => {
		if (discoveredSet.has(k)) ordered.push(k);
	});

	return ordered;
};

// ============================================================================
// STACKED BAR CHART FUNCTIONS (Horizontal)
// ============================================================================

/**
 * Calculate n-size range from row-level n_size across all items.
 * Used for stacked bar column generation to match chart view.
 */
const getItemsNSizeRange = (items: ChartDataItem[]): { minValue: number; maxValue: number; highlightMin: boolean; highlightMax: boolean } => {
	const values: number[] = [];
	items.forEach((item) => collectNonZeroNSizes(item, values));

	if (values.length > 0) {
		const minSize = Math.min(...values);
		const maxSize = Math.max(...values);
		return { minValue: minSize, maxValue: maxSize, highlightMin: minSize < 30, highlightMax: maxSize < 30 };
	}

	return { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };
};

const generateStackedBarColumns = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): ColumnConfig[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isPercentage = isPercentageData(chartData);
	// Reorder waves using authoritative ser_color order
	const waves = resolveSeriesOrder(extractWaveNames(chartData), seriesOrder);
	const columns: ColumnConfig[] = [];

	// Attribute column (always present)
	columns.push({
		title: "",
		dataIndex: "attribute",
		key: "attribute",
		fixed: "left",
		width: getColumnWidthFromConfig("attribute"),
		align: "center" as const,
	});

	// Wave column (only for hierarchy >= 3)
	if (hierarchyLevel >= 3) {
		columns.push({
			title: "",
			dataIndex: "wave",
			key: "wave",
			fixed: "left",
			width: getColumnWidthFromConfig("wave"),
			align: "center" as const,
		});
	}

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];

		// Use row-level n_size to match chart view behavior
		const flatNSizeData = getItemsNSizeRange(items);

		waves.forEach((wave, index) => {
			const nSizeData = flatNSizeData;

			columns.push({
				title: wave,
				dataIndex: wave,
				key: wave,
				width: getColumnWidthFromConfig("numerical"),
				align: "center" as const,
				isPercentage,
				_specialty: wave,
				_nSizeData: nSizeData,
				_isEvenGroup: index % 2 === 0,
				_hierarchyLevel: 0,
				_isLeafLevel: true,
			});
		});
	} else if (hierarchyLevel === 1) {
		// For hierarchy 1, create structure: Level1 -> Waves
		// Each level1 key becomes parent, each wave becomes a leaf column
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const items = chartData[key1];

			// Pre-calculate row-level n-size for this hierarchy group
			const groupNSizeData = isChartDataItemArray(items) ? getItemsNSizeRange(items) : { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };

			const waveColumns: ColumnConfig[] = waves.map((wave) => {
				const nSizeData = groupNSizeData;

				const isEven = columnGroupIndex % 2 === 0;
				columnGroupIndex++;

				return {
					title: wave,
					dataIndex: `${key1}__${wave}`,
					key: `${key1}__${wave}`,
					width: getColumnWidthFromConfig("numerical"),
					align: "center" as const,
					isPercentage,
					_specialty: wave,
					_nSizeData: nSizeData,
					_isEvenGroup: isEven,
					_hierarchyLevel: 1,
					_isLeafLevel: true,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: waveColumns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 2) {
		// For hierarchy = 2: Structure is Level1 -> Level2(last) -> Waves
		// No wave column, waves are data columns
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const items = chartData[key1][key2];

				// Use row-level n-size to match chart view behavior
				const level2NSize = isChartDataItemArray(items) ? getItemsNSizeRange(items) : { minValue: 0, maxValue: 0, highlightMin: false, highlightMax: false };

				const waveColumns: ColumnConfig[] = waves.map((wave) => {
					const nSizeData = level2NSize;

					const isEven = columnGroupIndex % 2 === 0;
					columnGroupIndex++;

					return {
						title: wave,
						dataIndex: `${key1}__${key2}__${wave}`,
						key: `${key1}__${key2}__${wave}`,
						width: getColumnWidthFromConfig("numerical"),
						align: "center" as const,
						isPercentage,
						_specialty: wave,
						_nSizeData: nSizeData,
						_isEvenGroup: isEven,
						_hierarchyLevel: 2,
						_isLeafLevel: true,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: waveColumns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 3) {
		// For hierarchy = 3
		// Structure: Level1 -> Level2 -> Series (waves become data columns, Level3 goes to wave column)
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				// For each level2, aggregate n-size across all level3 items
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);
				const allLevel3Items: ChartDataItem[] = [];
				level3Keys.forEach((key3) => {
					const items = chartData[key1][key2][key3];
					if (isChartDataItemArray(items)) allLevel3Items.push(...items);
				});
				const level2NSize = getItemsNSizeRange(allLevel3Items);

				// For each level2, create columns for each wave (series)
				const waveColumns: ColumnConfig[] = waves.map((wave) => {
					const nSizeData = level2NSize;

					const isEven = columnGroupIndex % 2 === 0;
					columnGroupIndex++;

					return {
						title: wave,
						dataIndex: `${key1}__${key2}__${wave}`,
						key: `${key1}__${key2}__${wave}`,
						width: getColumnWidthFromConfig("numerical"),
						align: "center" as const,
						isPercentage,
						_specialty: wave,
						_nSizeData: nSizeData,
						_isEvenGroup: isEven,
						_hierarchyLevel: 2,
						_isLeafLevel: true,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: waveColumns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 4) {
		// For hierarchy = 4
		// Structure: Level1 -> Level2 -> Level3 -> Series (waves become data columns, Level4 goes to wave column)
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let columnGroupIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);

				const level3Columns: ColumnConfig[] = level3Keys.map((key3) => {
					// For each level3, aggregate n-size across all level4 items
					const level4Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2][key3]), filterHierarchy, 3);
					const allLevel4Items: ChartDataItem[] = [];
					level4Keys.forEach((key4) => {
						const items = chartData[key1][key2][key3][key4];
						if (isChartDataItemArray(items)) allLevel4Items.push(...items);
					});
					const level3NSize = getItemsNSizeRange(allLevel4Items);

					// For each level3, create columns for each wave (series)
					const waveColumns: ColumnConfig[] = waves.map((wave) => {
						const nSizeData = level3NSize;

						const isEven = columnGroupIndex % 2 === 0;
						columnGroupIndex++;

						return {
							title: wave,
							dataIndex: `${key1}__${key2}__${key3}__${wave}`,
							key: `${key1}__${key2}__${key3}__${wave}`,
							width: getColumnWidthFromConfig("numerical"),
							align: "center" as const,
							isPercentage,
							_specialty: wave,
							_nSizeData: nSizeData,
							_isEvenGroup: isEven,
							_hierarchyLevel: 3,
							_isLeafLevel: true,
						};
					});

					return {
						title: key3,
						key: `${key1}__${key2}__${key3}`,
						align: "center" as const,
						children: waveColumns,
						_hierarchyLevel: 2,
						_isLeafLevel: false,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: level3Columns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	}

	return columns;
};

const generateStackedBarTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): TableRow[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	// Reorder waves using authoritative ser_color order
	const waves = resolveSeriesOrder(extractWaveNames(chartData), seriesOrder);
	const attributes = extractAttributes(chartData);
	const tableData: TableRow[] = [];
	let rowIndex = 0;

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];
		const totalAttributes = items.length;

		items.forEach((item, itemIndex) => {
			const isLastAttribute = itemIndex === totalAttributes - 1;

			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: item.xAxis,
			};

			if (isLastAttribute) {
				row.isLastAttributeRow = true;
			}

			item.series.forEach((seriesData) => {
				row[seriesData.name] = seriesData.value === "No data" ? null : seriesData.value;
			});

			const statMarkers = collectStatMarkers(item.series, (s) => s.name);
			if (statMarkers) {
				row._statMarkers = statMarkers;
			}

			const nSizes = collectNSizes(item.series, (s) => s.name);
			if (nSizes) {
				row._nSizes = nSizes;
			}

			tableData.push(row);
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;

			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: attribute,
			};

			if (isLastAttribute) {
				row.isLastAttributeRow = true;
			}

			const seriesPairs: Array<{ seriesData: SeriesData; dataIndex: string }> = [];

			level1Keys.forEach((key1) => {
				const items = chartData[key1];
				if (isChartDataItemArray(items)) {
					const item = items.find((itm) => itm.xAxis === attribute);
					if (item) {
						waves.forEach((wave) => {
							const seriesData = item.series.find((s) => s.name === wave);
							row[`${key1}__${wave}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
							if (seriesData) {
								seriesPairs.push({ seriesData, dataIndex: `${key1}__${wave}` });
							}
						});
					} else {
						waves.forEach((wave) => {
							row[`${key1}__${wave}`] = null;
						});
					}
				}
			});

			const statMarkers = collectStatMarkersFromPairs(seriesPairs);
			if (statMarkers) {
				row._statMarkers = statMarkers;
			}

			const nSizes = collectNSizesFromPairs(seriesPairs);
			if (nSizes) {
				row._nSizes = nSizes;
			}

			tableData.push(row);
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;

			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: attribute,
			};

			if (isLastAttribute) {
				row.isLastAttributeRow = true;
			}

			const seriesPairs: Array<{ seriesData: SeriesData; dataIndex: string }> = [];

			level1Keys.forEach((key1) => {
				const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
				level2Keys.forEach((key2) => {
					const items = chartData[key1][key2];
					if (isChartDataItemArray(items)) {
						const item = items.find((itm) => itm.xAxis === attribute);
						if (item) {
							waves.forEach((wave) => {
								const seriesData = item.series.find((s) => s.name === wave);
								row[`${key1}__${key2}__${wave}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
								if (seriesData) {
									seriesPairs.push({ seriesData, dataIndex: `${key1}__${key2}__${wave}` });
								}
							});
						} else {
							waves.forEach((wave) => {
								row[`${key1}__${key2}__${wave}`] = null;
							});
						}
					}
				});
			});

			const statMarkers = collectStatMarkersFromPairs(seriesPairs);
			if (statMarkers) {
				row._statMarkers = statMarkers;
			}

			const nSizes = collectNSizesFromPairs(seriesPairs);
			if (nSizes) {
				row._nSizes = nSizes;
			}

			tableData.push(row);
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);

		// Aggregate level-3 keys across all level-1/level-2 branches, then reorder by filter_hierarchy
		const level3KeysSet = new Set<string>();
		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
			level2Keys.forEach((key2) => {
				Object.keys(chartData[key1][key2]).forEach((key3) => level3KeysSet.add(key3));
			});
		});
		// Apply authoritative order for level 3 (wave/row axis)
		const level3Keys = resolveKeysByHierarchyOrder(Array.from(level3KeysSet), filterHierarchy, 2);

		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const level3Length = level3Keys.length;

			level3Keys.forEach((level3Key, level3Index) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					attribute: attribute,
					wave: level3Key,
				};

				if (level3Index === 0) {
					row.attributeRowSpan = level3Length;
				} else {
					row.attributeRowSpan = 0;
				}

				if (level3Index === level3Length - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				const seriesPairs: Array<{ seriesData: SeriesData; dataIndex: string }> = [];

				level1Keys.forEach((key1) => {
					const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
					level2Keys.forEach((key2) => {
						if (chartData[key1][key2] && chartData[key1][key2][level3Key]) {
							const items = chartData[key1][key2][level3Key];
							if (isChartDataItemArray(items)) {
								const item = items.find((itm) => itm.xAxis === attribute);
								if (item) {
									waves.forEach((wave) => {
										const seriesData = item.series.find((s) => s.name === wave);
										row[`${key1}__${key2}__${wave}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
										if (seriesData) {
											seriesPairs.push({ seriesData, dataIndex: `${key1}__${key2}__${wave}` });
										}
									});
								} else {
									waves.forEach((wave) => {
										row[`${key1}__${key2}__${wave}`] = null;
									});
								}
							} else {
								waves.forEach((wave) => {
									row[`${key1}__${key2}__${wave}`] = null;
								});
							}
						} else {
							waves.forEach((wave) => {
								row[`${key1}__${key2}__${wave}`] = null;
							});
						}
					});
				});

				const statMarkers = collectStatMarkersFromPairs(seriesPairs);
				if (statMarkers) {
					row._statMarkers = statMarkers;
				}

				const nSizes = collectNSizesFromPairs(seriesPairs);
				if (nSizes) {
					row._nSizes = nSizes;
				}

				tableData.push(row);
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);

		// Aggregate level-4 keys across all branches, then reorder by filter_hierarchy
		const level4KeysSet = new Set<string>();
		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
			level2Keys.forEach((key2) => {
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);
				level3Keys.forEach((key3) => {
					Object.keys(chartData[key1][key2][key3]).forEach((key4) => level4KeysSet.add(key4));
				});
			});
		});
		// Apply authoritative order for level 4 (wave/row axis)
		const level4Keys = resolveKeysByHierarchyOrder(Array.from(level4KeysSet), filterHierarchy, 3);

		const totalAttributes = attributes.length;

		attributes.forEach((attribute, attrIndex) => {
			const isLastAttribute = attrIndex === totalAttributes - 1;
			const level4Length = level4Keys.length;

			level4Keys.forEach((level4Key, level4Index) => {
				const row: TableRow = {
					key: `row-${rowIndex++}`,
					attribute: attribute,
					wave: level4Key,
				};

				if (level4Index === 0) {
					row.attributeRowSpan = level4Length;
				} else {
					row.attributeRowSpan = 0;
				}

				if (level4Index === level4Length - 1 && isLastAttribute) {
					row.isLastAttributeRow = true;
				}

				const seriesPairs: Array<{ seriesData: SeriesData; dataIndex: string }> = [];

				level1Keys.forEach((key1) => {
					const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);
					level2Keys.forEach((key2) => {
						const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);
						level3Keys.forEach((key3) => {
							if (chartData[key1][key2][key3] && chartData[key1][key2][key3][level4Key]) {
								const items = chartData[key1][key2][key3][level4Key];
								if (isChartDataItemArray(items)) {
									const item = items.find((itm) => itm.xAxis === attribute);
									if (item) {
										waves.forEach((wave) => {
											const seriesData = item.series.find((s) => s.name === wave);
											row[`${key1}__${key2}__${key3}__${wave}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
											if (seriesData) {
												seriesPairs.push({ seriesData, dataIndex: `${key1}__${key2}__${key3}__${wave}` });
											}
										});
									} else {
										waves.forEach((wave) => {
											row[`${key1}__${key2}__${key3}__${wave}`] = null;
										});
									}
								} else {
									waves.forEach((wave) => {
										row[`${key1}__${key2}__${key3}__${wave}`] = null;
									});
								}
							} else {
								waves.forEach((wave) => {
									row[`${key1}__${key2}__${key3}__${wave}`] = null;
								});
							}
						});
					});
				});

				const statMarkers = collectStatMarkersFromPairs(seriesPairs);
				if (statMarkers) {
					row._statMarkers = statMarkers;
				}

				const nSizes = collectNSizesFromPairs(seriesPairs);
				if (nSizes) {
					row._nSizes = nSizes;
				}

				tableData.push(row);
			});
		});
	}

	return tableData;
};

export const getHStackedBarChartColumns = (chartData: ChartData, chartType?: string, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): ColumnConfig[] => {
	// Pass authoritative ordering hints down into column generation
	const columns = generateStackedBarColumns(chartData, filterHierarchy, seriesOrder);
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isFlatData = hierarchyLevel === 0;

	const wrappedColumns = wrapLeafColumnsForNSizeRow(columns, isFlatData, chartType);

	const totalNumericalCols = countNumericalColumns(wrappedColumns);
	const hasWave = wrappedColumns.some((c) => c.dataIndex === "wave");
	const hasAttribute = wrappedColumns.some((c) => c.dataIndex === "attribute");

	(wrappedColumns as any)._widthMetadata = calculateColumnWidths(totalNumericalCols, hasWave, hasAttribute);

	return wrappedColumns;
};

export const getHStackedBarChartTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): TableRow[] => {
	// Pass authoritative ordering hints down into table data generation
	return generateStackedBarTableData(chartData, filterHierarchy, seriesOrder);
};

export const getStackedBarChartSummary = (chartData: ChartData) => {
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const waves = extractWaveNames(chartData);
	const attributes = extractAttributes(chartData);

	return {
		hierarchyLevel,
		totalAttributes: attributes.length,
		totalWaves: waves.length,
		totalRows: attributes.length * waves.length,
		waves,
		attributes,
	};
};

// ============================================================================
// STACKED COLUMN CHART FUNCTIONS
// ============================================================================

const generateStackedColumnColumns = (
	chartData: ChartData,
	filterHierarchy?: IHierarchyDefLite[],
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_seriesOrder?: string[],
): ColumnConfig[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isPercentage = isPercentageData(chartData);
	// Reorder xAxis values using filter_hierarchy at the deepest depth (= hierarchyLevel)
	const xAxisValues = resolveKeysByHierarchyOrder(extractAttributes(chartData), filterHierarchy, hierarchyLevel);
	const columns: ColumnConfig[] = [];

	// Attribute column (no wave column for stacked column)
	columns.push({
		title: "",
		dataIndex: "attribute",
		key: "attribute",
		fixed: "left",
		width: getColumnWidthFromConfig("attribute"),
		align: "center" as const,
	});

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];

		xAxisValues.forEach((xAxisVal, index) => {
			const nSizeData = getAttributeNSize(items, xAxisVal);

			columns.push({
				title: xAxisVal,
				dataIndex: xAxisVal,
				key: xAxisVal,
				width: getColumnWidthFromConfig("numerical"),
				align: "center" as const,
				isPercentage,
				_attributeName: xAxisVal,
				_nSizeData: {
					minValue: nSizeData.minValue,
					maxValue: nSizeData.maxValue,
					highlightMin: nSizeData.highlightMin,
					highlightMax: nSizeData.highlightMax,
				},
				_isEvenGroup: index % 2 === 0,
				_hierarchyLevel: 0,
				_isLeafLevel: true,
			});
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const items = chartData[key1];

			const xAxisColumns: ColumnConfig[] = xAxisValues.map((xAxisVal) => {
				const nSizeData = getAttributeNSize(items, xAxisVal);
				const isEven = globalColumnIndex % 2 === 0;
				globalColumnIndex++;

				return {
					title: xAxisVal,
					dataIndex: `${key1}__${xAxisVal}`,
					key: `${key1}__${xAxisVal}`,
					width: getColumnWidthFromConfig("numerical"),
					align: "center" as const,
					isPercentage,
					_attributeName: xAxisVal,
					_nSizeData: {
						minValue: nSizeData.minValue,
						maxValue: nSizeData.maxValue,
						highlightMin: nSizeData.highlightMin,
						highlightMax: nSizeData.highlightMax,
					},
					_isEvenGroup: isEven,
					_hierarchyLevel: 1,
					_isLeafLevel: true,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: xAxisColumns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const items = chartData[key1][key2];

				const xAxisColumns: ColumnConfig[] = xAxisValues.map((xAxisVal) => {
					const nSizeData = getAttributeNSize(items, xAxisVal);
					const isEven = globalColumnIndex % 2 === 0;
					globalColumnIndex++;

					return {
						title: xAxisVal,
						dataIndex: `${key1}__${key2}__${xAxisVal}`,
						key: `${key1}__${key2}__${xAxisVal}`,
						width: getColumnWidthFromConfig("numerical"),
						align: "center" as const,
						isPercentage,
						_attributeName: xAxisVal,
						_nSizeData: {
							minValue: nSizeData.minValue,
							maxValue: nSizeData.maxValue,
							highlightMin: nSizeData.highlightMin,
							highlightMax: nSizeData.highlightMax,
						},
						_isEvenGroup: isEven,
						_hierarchyLevel: 2,
						_isLeafLevel: true,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: xAxisColumns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = resolveKeysByHierarchyOrder(Object.keys(chartData), filterHierarchy, 0);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1]), filterHierarchy, 1);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const level3Keys = resolveKeysByHierarchyOrder(Object.keys(chartData[key1][key2]), filterHierarchy, 2);

				const level3Columns: ColumnConfig[] = level3Keys.map((key3) => {
					const items = chartData[key1][key2][key3];

					const xAxisColumns: ColumnConfig[] = xAxisValues.map((xAxisVal) => {
						const nSizeData = getAttributeNSize(items, xAxisVal);
						const isEven = globalColumnIndex % 2 === 0;
						globalColumnIndex++;

						return {
							title: xAxisVal,
							dataIndex: `${key1}__${key2}__${key3}__${xAxisVal}`,
							key: `${key1}__${key2}__${key3}__${xAxisVal}`,
							width: getColumnWidthFromConfig("numerical"),
							align: "center" as const,
							isPercentage,
							_attributeName: xAxisVal,
							_nSizeData: {
								minValue: nSizeData.minValue,
								maxValue: nSizeData.maxValue,
								highlightMin: nSizeData.highlightMin,
								highlightMax: nSizeData.highlightMax,
							},
							_isEvenGroup: isEven,
							_hierarchyLevel: 3,
							_isLeafLevel: true,
						};
					});

					return {
						title: key3,
						key: `${key1}__${key2}__${key3}`,
						align: "center" as const,
						children: xAxisColumns,
						_hierarchyLevel: 2,
						_isLeafLevel: false,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: level3Columns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = Object.keys(chartData);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			const level2Columns: ColumnConfig[] = level2Keys.map((key2) => {
				const level3Keys = Object.keys(chartData[key1][key2]);

				const level3Columns: ColumnConfig[] = level3Keys.map((key3) => {
					const level4Keys = Object.keys(chartData[key1][key2][key3]);

					const level4Columns: ColumnConfig[] = level4Keys.map((key4) => {
						const items = chartData[key1][key2][key3][key4];

						const xAxisColumns: ColumnConfig[] = xAxisValues.map((xAxisVal) => {
							const nSizeData = getAttributeNSize(items, xAxisVal);
							const isEven = globalColumnIndex % 2 === 0;
							globalColumnIndex++;

							return {
								title: xAxisVal,
								dataIndex: `${key1}__${key2}__${key3}__${key4}__${xAxisVal}`,
								key: `${key1}__${key2}__${key3}__${key4}__${xAxisVal}`,
								width: getColumnWidthFromConfig("numerical"),
								align: "center" as const,
								isPercentage,
								_attributeName: xAxisVal,
								_nSizeData: {
									minValue: nSizeData.minValue,
									maxValue: nSizeData.maxValue,
									highlightMin: nSizeData.highlightMin,
									highlightMax: nSizeData.highlightMax,
								},
								_isEvenGroup: isEven,
								_hierarchyLevel: 4,
								_isLeafLevel: true,
							};
						});

						return {
							title: key4,
							key: `${key1}__${key2}__${key3}__${key4}`,
							align: "center" as const,
							children: xAxisColumns,
							_hierarchyLevel: 3,
							_isLeafLevel: false,
						};
					});

					return {
						title: key3,
						key: `${key1}__${key2}__${key3}`,
						align: "center" as const,
						children: level4Columns,
						_hierarchyLevel: 2,
						_isLeafLevel: false,
					};
				});

				return {
					title: key2,
					key: `${key1}__${key2}`,
					align: "center" as const,
					children: level3Columns,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	}

	return columns;
};

const generateStackedColumnTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): TableRow[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	// Reorder xAxis values (column dataIndexes in v_stacked table) by filter_hierarchy
	const xAxisValues = resolveKeysByHierarchyOrder(extractAttributes(chartData), filterHierarchy, hierarchyLevel);
	// Reorder series names (which become row attribute values in v_stacked table) by ser_color
	const seriesNames = resolveSeriesOrder(extractWaveNames(chartData), seriesOrder);
	const tableData: TableRow[] = [];
	let rowIndex = 0;

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];

		seriesNames.forEach((seriesName) => {
			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: seriesName,
				wave: "",
			};

			// Collect stat markers keyed by xAxis (matches column dataIndex)
			const allSeriesForName: SeriesData[] = [];

			items.forEach((item) => {
				const seriesData = item.series.find((s) => s.name === seriesName);
				row[item.xAxis] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;

				if (seriesData) {
					allSeriesForName.push({ ...seriesData, name: item.xAxis });
				}
			});

			const statMarkers = collectStatMarkers(allSeriesForName, (s) => s.name);
			if (statMarkers) {
				row._statMarkers = statMarkers;
			}

			const nSizes = collectNSizes(allSeriesForName, (s) => s.name);
			if (nSizes) {
				row._nSizes = nSizes;
			}

			tableData.push(row);
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = Object.keys(chartData);

		seriesNames.forEach((seriesName) => {
			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: seriesName,
				wave: "",
			};

			level1Keys.forEach((key1) => {
				const items = chartData[key1];
				if (isChartDataItemArray(items)) {
					xAxisValues.forEach((xAxisVal) => {
						const item = items.find((itm) => itm.xAxis === xAxisVal);
						if (item) {
							const seriesData = item.series.find((s) => s.name === seriesName);
							row[`${key1}__${xAxisVal}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
							if (seriesData) {
								assignStatMarker(row, `${key1}__${xAxisVal}`, seriesData);
								assignNSize(row, `${key1}__${xAxisVal}`, seriesData);
							}
						} else {
							row[`${key1}__${xAxisVal}`] = null;
						}
					});
				}
			});

			tableData.push(row);
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = Object.keys(chartData);

		seriesNames.forEach((seriesName) => {
			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: seriesName,
				wave: "",
			};

			level1Keys.forEach((key1) => {
				const level2Keys = Object.keys(chartData[key1]);

				level2Keys.forEach((key2) => {
					const items = chartData[key1][key2];
					if (isChartDataItemArray(items)) {
						xAxisValues.forEach((xAxisVal) => {
							const item = items.find((itm) => itm.xAxis === xAxisVal);
							if (item) {
								const seriesData = item.series.find((s) => s.name === seriesName);
								row[`${key1}__${key2}__${xAxisVal}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
								if (seriesData) {
									assignStatMarker(row, `${key1}__${key2}__${xAxisVal}`, seriesData);
									assignNSize(row, `${key1}__${key2}__${xAxisVal}`, seriesData);
								}
							} else {
								row[`${key1}__${key2}__${xAxisVal}`] = null;
							}
						});
					}
				});
			});

			tableData.push(row);
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = Object.keys(chartData);

		seriesNames.forEach((seriesName) => {
			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: seriesName,
				wave: "",
			};

			level1Keys.forEach((key1) => {
				const level2Keys = Object.keys(chartData[key1]);

				level2Keys.forEach((key2) => {
					const level3Keys = Object.keys(chartData[key1][key2]);

					level3Keys.forEach((key3) => {
						const items = chartData[key1][key2][key3];
						if (isChartDataItemArray(items)) {
							xAxisValues.forEach((xAxisVal) => {
								const item = items.find((itm) => itm.xAxis === xAxisVal);
								if (item) {
									const seriesData = item.series.find((s) => s.name === seriesName);
									row[`${key1}__${key2}__${key3}__${xAxisVal}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
									if (seriesData) {
										assignStatMarker(row, `${key1}__${key2}__${key3}__${xAxisVal}`, seriesData);
										assignNSize(row, `${key1}__${key2}__${key3}__${xAxisVal}`, seriesData);
									}
								} else {
									row[`${key1}__${key2}__${key3}__${xAxisVal}`] = null;
								}
							});
						}
					});
				});
			});

			tableData.push(row);
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = Object.keys(chartData);

		seriesNames.forEach((seriesName) => {
			const row: TableRow = {
				key: `row-${rowIndex++}`,
				attribute: seriesName,
				wave: "",
			};

			level1Keys.forEach((key1) => {
				const level2Keys = Object.keys(chartData[key1]);

				level2Keys.forEach((key2) => {
					const level3Keys = Object.keys(chartData[key1][key2]);

					level3Keys.forEach((key3) => {
						const level4Keys = Object.keys(chartData[key1][key2][key3]);

						level4Keys.forEach((key4) => {
							const items = chartData[key1][key2][key3][key4];
							if (isChartDataItemArray(items)) {
								xAxisValues.forEach((xAxisVal) => {
									const item = items.find((itm) => itm.xAxis === xAxisVal);
									if (item) {
										const seriesData = item.series.find((s) => s.name === seriesName);
										row[`${key1}__${key2}__${key3}__${key4}__${xAxisVal}`] = seriesData && seriesData.value !== "No data" ? seriesData.value : null;
										if (seriesData) {
											assignStatMarker(row, `${key1}__${key2}__${key3}__${key4}__${xAxisVal}`, seriesData);
											assignNSize(row, `${key1}__${key2}__${key3}__${key4}__${xAxisVal}`, seriesData);
										}
									} else {
										row[`${key1}__${key2}__${key3}__${key4}__${xAxisVal}`] = null;
									}
								});
							}
						});
					});
				});
			});

			tableData.push(row);
		});
	}

	return tableData;
};

export const getVStackedBarChartColumns = (chartData: ChartData, chartType?: string, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): ColumnConfig[] => {
	const columns = generateStackedColumnColumns(chartData, filterHierarchy, seriesOrder);
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isFlatData = hierarchyLevel === 0;

	const wrappedColumns = wrapLeafColumnsForNSizeRow(columns, isFlatData, chartType);

	const totalNumericalCols = countNumericalColumns(wrappedColumns);
	const hasWave = false;
	const hasAttribute = wrappedColumns.some((c) => c.dataIndex === "attribute");

	(wrappedColumns as any)._widthMetadata = calculateColumnWidths(totalNumericalCols, hasWave, hasAttribute);

	return wrappedColumns;
};

export const getVStackedBarChartTableData = (chartData: ChartData, filterHierarchy?: IHierarchyDefLite[], seriesOrder?: string[]): TableRow[] => {
	return generateStackedColumnTableData(chartData, filterHierarchy, seriesOrder);
};

export const getVStackedColumnChartSummary = (chartData: ChartData) => {
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const seriesNames = extractWaveNames(chartData);
	const xAxisValues = extractAttributes(chartData);

	return {
		hierarchyLevel,
		totalAttributes: seriesNames.length,
		totalWaves: xAxisValues.length,
		totalRows: seriesNames.length,
		waves: xAxisValues,
		attributes: seriesNames,
	};
};

// ============================================================================
// CLUSTER COLUMN CHART FUNCTIONS
// ============================================================================

const generateClusterColumnColumns = (chartData: ChartData): ColumnConfig[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isPercentage = isPercentageData(chartData);
	const columns: ColumnConfig[] = [];

	// No attribute or wave column for cluster column

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];

		const allSeriesNames = new Set<string>();
		items.forEach((item) => {
			item.series.forEach((s) => allSeriesNames.add(s.name));
		});
		const seriesNames = Array.from(allSeriesNames);

		let globalColumnIndex = 0;
		seriesNames.forEach((seriesName) => {
			const seriesNSizeRange = getSeriesNSizeRange(items, seriesName);

			const waveColumns: ColumnConfig[] = items
				.map((item) => {
					const seriesData = item.series.find((s) => s.name === seriesName);
					if (!seriesData) return null;

					const isEven = globalColumnIndex % 2 === 0;
					globalColumnIndex++;

					return {
						title: item.xAxis,
						dataIndex: `${seriesName}__${item.xAxis}`,
						key: `${seriesName}__${item.xAxis}`,
						width: getColumnWidthFromConfig("numerical"),
						align: "center" as const,
						isPercentage,
						_isEvenGroup: isEven,
						_attributeName: item.xAxis,
						_nSizeData: {
							minValue: seriesData.n_size,
							maxValue: seriesData.n_size,
							highlightMin: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
							highlightMax: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
						},
						_hierarchyLevel: 1,
						_isLeafLevel: true,
					};
				})
				.filter(Boolean) as ColumnConfig[];

			columns.push({
				title: seriesName,
				key: seriesName,
				align: "center" as const,
				children: waveColumns,
				_nSizeData: seriesNSizeRange,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = Object.keys(chartData);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const items = chartData[key1];
			if (!isChartDataItemArray(items)) return;

			const allSeriesNames = new Set<string>();
			items.forEach((item) => {
				item.series.forEach((s) => allSeriesNames.add(s.name));
			});
			const seriesNames = Array.from(allSeriesNames);

			const seriesColumns: ColumnConfig[] = seriesNames.map((seriesName) => {
				const seriesNSizeRange = getSeriesNSizeRange(items, seriesName);

				const waveColumns: ColumnConfig[] = items
					.map((item) => {
						const seriesData = item.series.find((s) => s.name === seriesName);
						if (!seriesData) return null;

						const isEven = globalColumnIndex % 2 === 0;
						globalColumnIndex++;

						return {
							title: item.xAxis,
							dataIndex: `${key1}__${seriesName}__${item.xAxis}`,
							key: `${key1}__${seriesName}__${item.xAxis}`,
							width: getColumnWidthFromConfig("numerical"),
							align: "center" as const,
							isPercentage,
							_isEvenGroup: isEven,
							_attributeName: item.xAxis,
							_nSizeData: {
								minValue: seriesData.n_size,
								maxValue: seriesData.n_size,
								highlightMin: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
								highlightMax: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
							},
							_hierarchyLevel: 2,
							_isLeafLevel: true,
						};
					})
					.filter(Boolean) as ColumnConfig[];

				return {
					title: seriesName,
					key: `${key1}__${seriesName}`,
					align: "center" as const,
					children: waveColumns,
					_nSizeData: seriesNSizeRange,
					_hierarchyLevel: 1,
					_isLeafLevel: false,
				};
			});

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: seriesColumns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = Object.keys(chartData);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			const level2Columns: ColumnConfig[] = level2Keys
				.map((key2) => {
					const items = chartData[key1][key2];
					if (!isChartDataItemArray(items)) return null;

					const allSeriesNames = new Set<string>();
					items.forEach((item) => {
						item.series.forEach((s) => allSeriesNames.add(s.name));
					});
					const seriesNames = Array.from(allSeriesNames);

					const seriesColumns: ColumnConfig[] = seriesNames.map((seriesName) => {
						const seriesNSizeRange = getSeriesNSizeRange(items, seriesName);

						const waveColumns: ColumnConfig[] = items
							.map((item) => {
								const seriesData = item.series.find((s) => s.name === seriesName);
								if (!seriesData) return null;

								const isEven = globalColumnIndex % 2 === 0;
								globalColumnIndex++;

								return {
									title: item.xAxis,
									dataIndex: `${key1}__${key2}__${seriesName}__${item.xAxis}`,
									key: `${key1}__${key2}__${seriesName}__${item.xAxis}`,
									width: getColumnWidthFromConfig("numerical"),
									align: "center" as const,
									isPercentage,
									_isEvenGroup: isEven,
									_attributeName: item.xAxis,
									_nSizeData: {
										minValue: seriesData.n_size,
										maxValue: seriesData.n_size,
										highlightMin: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
										highlightMax: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
									},
									_hierarchyLevel: 3,
									_isLeafLevel: true,
								};
							})
							.filter(Boolean) as ColumnConfig[];

						return {
							title: seriesName,
							key: `${key1}__${key2}__${seriesName}`,
							align: "center" as const,
							children: waveColumns,
							_nSizeData: seriesNSizeRange,
							_hierarchyLevel: 2,
							_isLeafLevel: false,
						};
					});

					return {
						title: key2,
						key: `${key1}__${key2}`,
						align: "center" as const,
						children: seriesColumns,
						_hierarchyLevel: 1,
						_isLeafLevel: false,
					};
				})
				.filter(Boolean) as ColumnConfig[];

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = Object.keys(chartData);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			const level2Columns: ColumnConfig[] = level2Keys
				.map((key2) => {
					const level3Keys = Object.keys(chartData[key1][key2]);

					const level3Columns: ColumnConfig[] = level3Keys
						.map((key3) => {
							const items = chartData[key1][key2][key3];
							if (!isChartDataItemArray(items)) return null;

							const allSeriesNames = new Set<string>();
							items.forEach((item) => {
								item.series.forEach((s) => allSeriesNames.add(s.name));
							});
							const seriesNames = Array.from(allSeriesNames);

							const seriesColumns: ColumnConfig[] = seriesNames.map((seriesName) => {
								const seriesNSizeRange = getSeriesNSizeRange(items, seriesName);

								const waveColumns: ColumnConfig[] = items
									.map((item) => {
										const seriesData = item.series.find((s) => s.name === seriesName);
										if (!seriesData) return null;

										const isEven = globalColumnIndex % 2 === 0;
										globalColumnIndex++;

										return {
											title: item.xAxis,
											dataIndex: `${key1}__${key2}__${key3}__${seriesName}__${item.xAxis}`,
											key: `${key1}__${key2}__${key3}__${seriesName}__${item.xAxis}`,
											width: getColumnWidthFromConfig("numerical"),
											align: "center" as const,
											isPercentage,
											_isEvenGroup: isEven,
											_attributeName: item.xAxis,
											_nSizeData: {
												minValue: seriesData.n_size,
												maxValue: seriesData.n_size,
												highlightMin: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
												highlightMax: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
											},
											_hierarchyLevel: 4,
											_isLeafLevel: true,
										};
									})
									.filter(Boolean) as ColumnConfig[];

								return {
									title: seriesName,
									key: `${key1}__${key2}__${key3}__${seriesName}`,
									align: "center" as const,
									children: waveColumns,
									_nSizeData: seriesNSizeRange,
									_hierarchyLevel: 3,
									_isLeafLevel: false,
								};
							});

							return {
								title: key3,
								key: `${key1}__${key2}__${key3}`,
								align: "center" as const,
								children: seriesColumns,
								_hierarchyLevel: 2,
								_isLeafLevel: false,
							};
						})
						.filter(Boolean) as ColumnConfig[];

					return {
						title: key2,
						key: `${key1}__${key2}`,
						align: "center" as const,
						children: level3Columns,
						_hierarchyLevel: 1,
						_isLeafLevel: false,
					};
				})
				.filter(Boolean) as ColumnConfig[];

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = Object.keys(chartData);
		let globalColumnIndex = 0;

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			const level2Columns: ColumnConfig[] = level2Keys
				.map((key2) => {
					const level3Keys = Object.keys(chartData[key1][key2]);

					const level3Columns: ColumnConfig[] = level3Keys
						.map((key3) => {
							const level4Keys = Object.keys(chartData[key1][key2][key3]);

							const level4Columns: ColumnConfig[] = level4Keys
								.map((key4) => {
									const items = chartData[key1][key2][key3][key4];
									if (!isChartDataItemArray(items)) return null;

									const allSeriesNames = new Set<string>();
									items.forEach((item) => {
										item.series.forEach((s) => allSeriesNames.add(s.name));
									});
									const seriesNames = Array.from(allSeriesNames);

									const seriesColumns: ColumnConfig[] = seriesNames.map((seriesName) => {
										const seriesNSizeRange = getSeriesNSizeRange(items, seriesName);

										const waveColumns: ColumnConfig[] = items
											.map((item) => {
												const seriesData = item.series.find((s) => s.name === seriesName);
												if (!seriesData) return null;

												const isEven = globalColumnIndex % 2 === 0;
												globalColumnIndex++;

												return {
													title: item.xAxis,
													dataIndex: `${key1}__${key2}__${key3}__${key4}__${seriesName}__${item.xAxis}`,
													key: `${key1}__${key2}__${key3}__${key4}__${seriesName}__${item.xAxis}`,
													width: getColumnWidthFromConfig("numerical"),
													align: "center" as const,
													isPercentage,
													_isEvenGroup: isEven,
													_attributeName: item.xAxis,
													_nSizeData: {
														minValue: seriesData.n_size,
														maxValue: seriesData.n_size,
														highlightMin: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
														highlightMax: typeof seriesData.n_size === "number" && seriesData.n_size < 30,
													},
													_hierarchyLevel: 5,
													_isLeafLevel: true,
												};
											})
											.filter(Boolean) as ColumnConfig[];

										return {
											title: seriesName,
											key: `${key1}__${key2}__${key3}__${key4}__${seriesName}`,
											align: "center" as const,
											children: waveColumns,
											_nSizeData: seriesNSizeRange,
											_hierarchyLevel: 4,
											_isLeafLevel: false,
										};
									});

									return {
										title: key4,
										key: `${key1}__${key2}__${key3}__${key4}`,
										align: "center" as const,
										children: seriesColumns,
										_hierarchyLevel: 3,
										_isLeafLevel: false,
									};
								})
								.filter(Boolean) as ColumnConfig[];

							return {
								title: key3,
								key: `${key1}__${key2}__${key3}`,
								align: "center" as const,
								children: level4Columns,
								_hierarchyLevel: 2,
								_isLeafLevel: false,
							};
						})
						.filter(Boolean) as ColumnConfig[];

					return {
						title: key2,
						key: `${key1}__${key2}`,
						align: "center" as const,
						children: level3Columns,
						_hierarchyLevel: 1,
						_isLeafLevel: false,
					};
				})
				.filter(Boolean) as ColumnConfig[];

			columns.push({
				title: key1,
				key: key1,
				align: "center" as const,
				children: level2Columns,
				_hierarchyLevel: 0,
				_isLeafLevel: false,
			});
		});
	}

	return columns;
};

const generateClusterColumnTableData = (chartData: ChartData): TableRow[] => {
	if (!chartData) return [];

	const hierarchyLevel = detectHierarchyLevel(chartData);
	const tableData: TableRow[] = [];

	const row: TableRow = {
		key: "data-row-0",
	};

	if (hierarchyLevel === 0) {
		const items = chartData as ChartDataItem[];

		items.forEach((item) => {
			item.series.forEach((seriesData) => {
				const dataIndex = `${seriesData.name}__${item.xAxis}`;
				row[dataIndex] = seriesData.value === "No data" ? null : seriesData.value;
				assignStatMarker(row, dataIndex, seriesData);
				assignNSize(row, dataIndex, seriesData);
			});
		});
	} else if (hierarchyLevel === 1) {
		const level1Keys = Object.keys(chartData);

		level1Keys.forEach((key1) => {
			const items = chartData[key1];
			if (isChartDataItemArray(items)) {
				items.forEach((item) => {
					item.series.forEach((seriesData) => {
						const dataIndex = `${key1}__${seriesData.name}__${item.xAxis}`;
						row[dataIndex] = seriesData.value === "No data" ? null : seriesData.value;
						assignStatMarker(row, dataIndex, seriesData);
						assignNSize(row, dataIndex, seriesData);
					});
				});
			}
		});
	} else if (hierarchyLevel === 2) {
		const level1Keys = Object.keys(chartData);

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			level2Keys.forEach((key2) => {
				const items = chartData[key1][key2];
				if (isChartDataItemArray(items)) {
					items.forEach((item) => {
						item.series.forEach((seriesData) => {
							const dataIndex = `${key1}__${key2}__${seriesData.name}__${item.xAxis}`;
							row[dataIndex] = seriesData.value === "No data" ? null : seriesData.value;
							assignStatMarker(row, dataIndex, seriesData);
							assignNSize(row, dataIndex, seriesData);
						});
					});
				}
			});
		});
	} else if (hierarchyLevel === 3) {
		const level1Keys = Object.keys(chartData);

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			level2Keys.forEach((key2) => {
				const level3Keys = Object.keys(chartData[key1][key2]);

				level3Keys.forEach((key3) => {
					const items = chartData[key1][key2][key3];
					if (isChartDataItemArray(items)) {
						items.forEach((item) => {
							item.series.forEach((seriesData) => {
								const dataIndex = `${key1}__${key2}__${key3}__${seriesData.name}__${item.xAxis}`;
								row[dataIndex] = seriesData.value === "No data" ? null : seriesData.value;
								assignStatMarker(row, dataIndex, seriesData);
								assignNSize(row, dataIndex, seriesData);
							});
						});
					}
				});
			});
		});
	} else if (hierarchyLevel === 4) {
		const level1Keys = Object.keys(chartData);

		level1Keys.forEach((key1) => {
			const level2Keys = Object.keys(chartData[key1]);

			level2Keys.forEach((key2) => {
				const level3Keys = Object.keys(chartData[key1][key2]);

				level3Keys.forEach((key3) => {
					const level4Keys = Object.keys(chartData[key1][key2][key3]);

					level4Keys.forEach((key4) => {
						const items = chartData[key1][key2][key3][key4];
						if (isChartDataItemArray(items)) {
							items.forEach((item) => {
								item.series.forEach((seriesData) => {
									const dataIndex = `${key1}__${key2}__${key3}__${key4}__${seriesData.name}__${item.xAxis}`;
									row[dataIndex] = seriesData.value === "No data" ? null : seriesData.value;
									assignStatMarker(row, dataIndex, seriesData);
									assignNSize(row, dataIndex, seriesData);
								});
							});
						}
					});
				});
			});
		});
	}

	tableData.push(row);
	return tableData;
};

export const getClusterColumnChartColumns = (chartData: ChartData, chartType?: string): ColumnConfig[] => {
	const columns = generateClusterColumnColumns(chartData);
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const isFlatData = hierarchyLevel === 0;

	const wrappedColumns = wrapLeafColumnsForNSizeRow(columns, isFlatData, chartType);

	const totalNumericalCols = countNumericalColumns(wrappedColumns);
	const hasWave = false;
	const hasAttribute = false;

	(wrappedColumns as any)._widthMetadata = calculateColumnWidths(totalNumericalCols, hasWave, hasAttribute);

	return wrappedColumns;
};

export const getClusterColumnChartTableData = (chartData: ChartData): TableRow[] => {
	return generateClusterColumnTableData(chartData);
};

export const getClusterColumnChartSummary = (chartData: ChartData) => {
	const hierarchyLevel = detectHierarchyLevel(chartData);
	const xAxisValues = extractAttributes(chartData);
	const seriesNames = extractWaveNames(chartData);

	return {
		hierarchyLevel,
		totalAttributes: xAxisValues.length,
		totalSeries: seriesNames.length,
		totalRows: xAxisValues.length,
		xAxisValues,
		seriesNames,
	};
};
